"use client";

import { useMemo, useState } from "react";
import { Input } from "@/app/_module/components/ui/input";
import { Progress } from "@/app/_module/components/ui/progress";
import { Trophy, Flame, Target, Search, AlertCircle, Check } from "lucide-react";
import { useAgentWorkload } from "@/app/(authenticated)/lead-assignment/_modules/hooks/queries";

/**
 * Agency-wide quota board: every agent's goal and how far along they are.
 *
 * Reuses `useAgentWorkload` rather than adding a parallel fetch — that hook already
 * returns goalType/goalValue/goalDuration/goalCurrent computed by the same backend pass
 * that powers the lead-assignment goal editor. Sharing it means this board and the place
 * quotas are SET can never disagree about a number, which is the whole point of the tab.
 */

// Mirrors GOAL_METRICS on the lead-assignment side and the agent's dashboard card.
const METRIC: Record<string, { label: string; unit: string; prefix?: string }> = {
  lead_count: { label: "Leads", unit: "" },
  dials: { label: "Dials", unit: "" },
  talk_time: { label: "Talk Time", unit: "m" },
  completed_calls: { label: "Completed Calls", unit: "" },
  contacts: { label: "Contacts", unit: "" },
  appointments: { label: "Appointments", unit: "" },
  premium: { label: "Premium Sold", unit: "", prefix: "$" },
  policies_submitted: { label: "Policies Submitted", unit: "" },
};

const PERIOD: Record<string, string> = {
  daily: "today",
  weekly: "this week",
  monthly: "this month",
};

interface Row {
  agentId: string;
  agentName: string;
  agencyName?: string;
  goalType: string;
  goalValue: number;
  goalCurrent: number;
  goalDuration: string;
  pct: number;
  /** 'agency' = inherited default (goalSourceName says whose); 'agent' = their own. */
  goalSource?: "agent" | "agency" | null;
  goalSourceName?: string | null;
}

export default function AgencyQuotaTracker() {
  const { data, isLoading } = useAgentWorkload();
  const [search, setSearch] = useState("");
  // Empty set = no filter (show everything). Modelling "none selected" as "all" rather
  // than "nothing" avoids the state where an owner unticks the last agency and the board
  // goes blank with no obvious way back.
  const [agencyFilter, setAgencyFilter] = useState<Set<string>>(new Set());

  const agents = (data as any)?.agents || [];

  // Distinct agencies present in the roster, for the filter chips. Derived from the data
  // rather than fetched separately, so it can only ever offer agencies that actually
  // have agents on this board.
  const agencies = useMemo(() => {
    const names = new Set<string>();
    for (const a of agents as any[]) if (a.agencyName) names.add(a.agencyName);
    return Array.from(names).sort();
  }, [agents]);

  const toggleAgency = (name: string) =>
    setAgencyFilter((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });

  const { withQuota, withoutQuota } = useMemo(() => {
    const withQ: Row[] = [];
    const withoutQ: { agentId: string; agentName: string; agencyName?: string }[] = [];

    for (const a of agents as any[]) {
      // Agency filter first — an owner narrowing to one sub-agency expects the SUMMARY
      // numbers above to narrow too, not just the list, so this has to happen before
      // the counts are derived.
      if (agencyFilter.size > 0 && !agencyFilter.has(a.agencyName || "")) continue;

      // Fall back to the legacy lead_count goal so agents configured before the
      // goalType system still appear, rather than looking like they have no quota.
      const gType = a.goalType || (a.leadGoal > 0 ? "lead_count" : null);
      const gValue = a.goalValue ?? (a.leadGoal > 0 ? a.leadGoal : null);
      if (!gType || !gValue || gValue <= 0) {
        withoutQ.push({ agentId: a.agentId, agentName: a.agentName, agencyName: a.agencyName });
        continue;
      }
      const current = a.goalType ? a.goalCurrent ?? 0 : a.completedLeads || 0;
      withQ.push({
        agentId: a.agentId,
        agentName: a.agentName,
        agencyName: a.agencyName,
        goalType: gType,
        goalValue: gValue,
        goalCurrent: current,
        goalDuration: a.goalDuration || "daily",
        pct: (current / gValue) * 100,
        goalSource: a.goalSource ?? null,
        goalSourceName: a.goalSourceName ?? null,
      });
    }

    const q = search.trim().toLowerCase();
    const match = (n: string, ag?: string) =>
      !q || `${n} ${ag || ""}`.toLowerCase().includes(q);

    return {
      // Furthest along first — an owner opening this wants the leaderboard read, and
      // whoever needs help falls to the bottom where the eye ends up anyway.
      withQuota: withQ.filter((r) => match(r.agentName, r.agencyName)).sort((a, b) => b.pct - a.pct),
      withoutQuota: withoutQ.filter((r) => match(r.agentName, r.agencyName)),
    };
  }, [agents, search, agencyFilter]);

  const hitCount = withQuota.filter((r) => r.goalCurrent >= r.goalValue).length;

  const avgPct = withQuota.length
    ? withQuota.reduce((s, r) => s + Math.min(r.pct, 100), 0) / withQuota.length
    : 0;

  const fmt = (n: number, t: string) => {
    const m = METRIC[t] || { label: t, unit: "" };
    return `${m.prefix || ""}${Number(n || 0).toLocaleString("en-US")}${m.unit || ""}`;
  };

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-16 animate-pulse rounded-lg bg-gray-100" />
        ))}
      </div>
    );
  }

  if (!agents.length) {
    return (
      <p className="text-sm text-muted-foreground py-8 text-center">
        No agents in this agency yet.
      </p>
    );
  }

  return (
    // FIXED-HEIGHT PANEL. The whole tracker occupies one bounded box and never grows
    // with the roster — only the agent list inside it scrolls.
    //
    // `max-h-[520px]` on the list alone wasn't enough: the summary strip, search, filter
    // chips and the "no quota" section still stacked underneath it, so a big downline
    // still ran the page on for ~1,000px. Bounding the CONTAINER and letting the list
    // flex into whatever is left is what actually fixes it.
    //
    // 72vh scales with the viewport rather than a hardcoded pixel height, so it fills a
    // large monitor without overflowing a laptop.
    <div className="flex flex-col h-[72vh] min-h-[420px]">
      {/* Summary strip — shrink-0 so the header block keeps its natural height and the
          list below absorbs all the flex. */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 shrink-0">
        <div className="rounded-lg border p-4">
          <p className="text-2xl font-bold text-gray-900">
            {hitCount}
            <span className="text-base font-medium text-gray-400"> / {withQuota.length}</span>
          </p>
          <p className="text-xs text-gray-500 mt-0.5">Agents at or over quota</p>
        </div>
        <div className="rounded-lg border p-4">
          <p className="text-2xl font-bold text-gray-900">{avgPct.toFixed(0)}%</p>
          <p className="text-xs text-gray-500 mt-0.5">Average completion</p>
        </div>
        {/* Deliberately a COUNT, not "the quota".
            A previous version showed a single headline target ("$12,000 · Premium Sold")
            picked as the most common. That is misleading the moment agents are on
            different metrics — which is the entire point of the per-agent override — so
            it implied a uniformity that may not exist. Each agent's own target is on
            their row, where it can't be wrong.
            Worded positively: "0 · Agents without a quota" reads correctly, unlike the
            original "0 · No quota set", which parsed as "zero quotas are set". */}
        <div className="rounded-lg border p-4">
          <p
            className={`text-2xl font-bold ${
              withoutQuota.length > 0 ? "text-amber-600" : "text-gray-900"
            }`}
          >
            {withoutQuota.length}
          </p>
          <p className="text-xs text-gray-500 mt-0.5">Agents without a quota</p>
        </div>
      </div>

      <div className="relative mt-4 shrink-0">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          placeholder="Search agents or agency…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
        />
      </div>

      {/* Agency filter. Only shown when there's more than one agency to choose between —
          a single-agency owner gets no value from a filter that can only ever be "all".
          Capped + scrollable too: 10+ agencies wrap to several rows and would otherwise
          eat the space meant for the list. */}
      {agencies.length > 1 && (
        <div className="flex flex-wrap items-center gap-2 mt-3 shrink-0 max-h-[76px] overflow-y-auto">
          <button
            type="button"
            onClick={() => setAgencyFilter(new Set())}
            className={`text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${
              agencyFilter.size === 0
                ? "bg-primary text-white border-primary"
                : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"
            }`}
          >
            All agencies
          </button>
          {agencies.map((name) => {
            const on = agencyFilter.has(name);
            return (
              <button
                key={name}
                type="button"
                onClick={() => toggleAgency(name)}
                className={`inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${
                  on
                    ? "bg-primary/10 text-primary border-primary/40"
                    : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"
                }`}
              >
                <span
                  className={`h-3 w-3 rounded-[3px] border flex items-center justify-center ${
                    on ? "bg-primary border-primary" : "border-gray-300"
                  }`}
                >
                  {on && <Check className="h-2.5 w-2.5 text-white" />}
                </span>
                {name}
              </button>
            );
          })}
        </div>
      )}

      {withQuota.length === 0 && withoutQuota.length === 0 && (
        <p className="text-sm text-muted-foreground py-6 text-center shrink-0">
          {search.trim()
            ? `No agents match “${search}”.`
            : "No agents in the selected agencies."}
        </p>
      )}

      {/* The ONLY scrolling region. `flex-1` takes whatever height is left in the fixed
          panel; `min-h-0` is the load-bearing part — without it a flex child refuses to
          shrink below its content size and overflow-y never engages, which is exactly why
          the earlier max-h attempt still let the page run on. */}
      <div className="flex-1 min-h-0 overflow-y-auto space-y-2 mt-4 pr-1">
        {withQuota.map((r) => {
          const hit = r.goalCurrent >= r.goalValue;
          const close = !hit && r.pct >= 75;
          const m = METRIC[r.goalType] || { label: r.goalType, unit: "" };
          return (
            <div
              key={r.agentId}
              className={`flex items-center gap-4 p-3 rounded-lg border ${
                hit ? "border-emerald-200 bg-emerald-50/40" : "border-gray-200"
              }`}
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium text-gray-900 truncate">{r.agentName}</p>
                  {r.agencyName && (
                    <span className="text-xs text-primary truncate">{r.agencyName}</span>
                  )}
                  {hit && (
                    <span className="shrink-0 inline-flex items-center gap-1 text-[10px] font-bold uppercase text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded">
                      <Trophy className="h-3 w-3" /> Hit
                    </span>
                  )}
                  {close && (
                    <span className="shrink-0 inline-flex items-center gap-1 text-[10px] font-bold uppercase text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">
                      <Flame className="h-3 w-3" /> Close
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-0.5">
                  {m.label} · {PERIOD[r.goalDuration] || r.goalDuration}
                  {r.goalSource === "agency" && (
                    <span className="ml-1.5 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-600" title={`Inherited from ${r.goalSourceName || "the agency"}'s default. Set a goal on this agent to override.`}>
                      {r.goalSourceName ? `${r.goalSourceName} default` : "agency default"}
                    </span>
                  )}
                </p>
                {/* Bar clamps at 100 so an overachiever can't overflow the track; the
                    percentage on the right stays uncapped. */}
                <Progress
                  className={`h-2 mt-2 ${hit ? "[&>div]:bg-emerald-500" : ""}`}
                  value={Math.min(r.pct, 100)}
                />
              </div>
              <div className="text-right shrink-0 w-28">
                <p
                  className={`text-sm font-bold ${
                    hit ? "text-emerald-600" : "text-gray-900"
                  }`}
                >
                  {fmt(r.goalCurrent, r.goalType)}
                  <span className="text-xs font-medium text-gray-400">
                    {" "}
                    / {fmt(r.goalValue, r.goalType)}
                  </span>
                </p>
                <p className="text-xs text-gray-500">{r.pct.toFixed(0)}%</p>
              </div>
            </div>
          );
        })}

      {/* Agents with no quota — surfaced, not hidden. An owner reviewing quota
          attainment needs to see who isn't being measured at all; otherwise the board
          silently flatters itself by only counting people who have targets.
          Lives INSIDE the scroll region: outside it, this block added its own height
          below the panel and the page ran on again — the exact thing being fixed. */}
      {withoutQuota.length > 0 && (
        <div className="pt-4 mt-2 border-t">
          <div className="flex items-center gap-2 mb-2">
            <AlertCircle className="h-4 w-4 text-amber-500" />
            <p className="text-sm font-medium text-gray-700">
              These {withoutQuota.length === 1 ? "agent has" : `${withoutQuota.length} agents have`} no
              quota yet
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {withoutQuota.map((a) => (
              <span
                key={a.agentId}
                className="inline-flex items-center gap-1.5 text-xs text-gray-600 bg-gray-100 px-2.5 py-1.5 rounded-md"
              >
                <Target className="h-3 w-3 text-gray-400" />
                {a.agentName}
                {a.agencyName && <span className="text-gray-400">· {a.agencyName}</span>}
              </span>
            ))}
          </div>
          <p className="text-xs text-gray-400 mt-2">
            Set quotas from Lead Assignment → Set Goals.
          </p>
        </div>
      )}
      </div>
    </div>
  );
}
